import { forwardRef, type ComponentPropsWithoutRef } from 'react'
import clsx from 'clsx'
import { button, type ButtonVariants } from './Button.css'

type ButtonProps = {
  /** Additional CSS class names */
  className?: string
} & ButtonVariants &
  ComponentPropsWithoutRef<'button'>

/**
 * A styled button component with variants.
 *
 * @example
 * ```tsx
 * <Button>Primary Button</Button>
 * <Button variant="secondary">Secondary</Button>
 * <Button variant="outline">Outline</Button>
 * <Button variant="muted">Muted</Button>
 * <Button variant="danger" size="sm">Delete</Button>
 * <Button variant="outlinePrimary">Outline Primary</Button>
 * <Button variant="outlineSecondary">Outline Secondary</Button>
 * <Button variant="outlineDanger" size="sm">Delete</Button>
 * <Button variant="ghost">Cancel</Button>
 * <Button width="full">Full Width</Button>
 * <Button width="fixed">Fixed Width</Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, width, fullWidth, className, type = 'button', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={clsx(button({ variant, size, width, fullWidth }), className)}
        {...props}
      />
    )
  }
)

Button.displayName = 'Button'
